/**
 * Registry Manifest + Rules — connector-owned configuration data.
 * BET-99 / ARCH-16 / ARCH-19 / TEN-2781
 *
 * SINGLE-HOMED HERE (Lane D S1): this is manifest+rules DATA, not algorithm — the
 * `path`/`exportName`/`keyExtraction` fields are LOCAL-resolver instructions (the
 * resolver needs them locally to parse the agent's checkout), so this stays
 * connector-side and is sent over the wire to `chain.evaluateCoherence` alongside the
 * locally-resolved slugs (kernel/codebaseCoherence/engine.ts owns the pure algorithm
 * only — no config of its own, mirrors kernel/relations/scoreLinkCandidates.ts's
 * connector-supplied collMap/hubSlugs). There used to be a top-level "canonical" copy
 * (src/lib/coherence/registry-manifest.ts + rules/collection-registries.ts) with no
 * live importer, byte-synced here by `scripts/sync-coherence-to-mcp.mjs` — both the
 * canonical copy and the sync script are retired in this same slice. The
 * `subset-renderers` invariant (renderers ids ⊆ classification-map ids) that used to
 * ride the cross-copy drift gate is now a single in-repo unit test — see
 * `__tests__/data-manifest-consistency.test.ts`.
 *
 * ENT-65 (FEAT-201): Cross-registry sync rules for CLASSIFICATION_MAP ↔ DEFAULT_COLLECTIONS
 * and CLASSIFICATION_MAP ↔ CLASSIFICATION_META were removed. These constants are seed-only.
 */

import type { CoherenceRule, RegistryRef } from "./types.js";

export const REGISTRY_MANIFEST: readonly RegistryRef[] = [
  // ── Canonical UI registries ──────────────────────────────────────
  // BET-143 Slice 4: ui-colors removed — color is now a field on collection docs (schema.ts).
  {
    id: "routes",
    path: "src/lib/navigation.ts",
    exportName: "COLLECTION_ROUTES",
    description: "Collection slug to dedicated route path",
    expectedCoverage: "curated",
    keyExtraction: "object-keys",
  },
  {
    id: "gap-paths",
    path: "src/lib/navigation.ts",
    exportName: "GAP_PATHS",
    description: "Readiness gap ID to workspace-relative route path",
    expectedCoverage: "curated",
    keyExtraction: "object-keys",
  },
  // BET-280 S3: display-fields removed — DISPLAY_FIELDS constant deleted, field display is now schema-driven.
  // BET-280 S3: brain-icons removed — COLLECTION_ICONS unexported, getCollectionIcon() is a local rendering fallback only.

  // ── Graph visualization registries ───────────────────────────────
  // BET-143 Slice 4: graph-colors and graph-type-colors removed.
  // Graph rendering uses local fallback palettes (canvas needs resolved hex/CSS vars, not DB reads).

  // ── Cortex registries ────────────────────────────────────────────
  {
    id: "renderers",
    path: "src/lib/components/studio/renderers/index.ts",
    exportName: "COLLECTION_RENDERERS",
    description: "Custom Svelte renderer component per collection",
    expectedCoverage: "subset",
    keyExtraction: "object-keys",
  },

  // ── Backend registries ───────────────────────────────────────────
  {
    id: "classification-map",
    path: "convex/lib/seedClassificationRegistry.ts",
    exportName: "SEED_CLASSIFICATION_REGISTRY",
    // ENT-65
    description: "Seed-only: governance, nav group, ID prefix. Runtime reads from DB collection doc.",
    expectedCoverage: "curated",
    keyExtraction: "array-slug",
  },
  // TEN-598: default-collections removed. Collection definitions live in system_collection_definitions (DB).
] as const;

export const COLLECTION_REGISTRY_RULES: readonly CoherenceRule[] = [
  // ── Subset: specialized registries should only reference known collections ──
  // BET-143 Slice 4: brain-colors, graph-colors, graph-type-colors rules removed —
  // those registries were deleted when color moved to collection docs.
  // BET-280 S3: brain-icons rule removed — COLLECTION_ICONS unexported, no longer a registry.
  // BET-280 S3: display-fields rule removed — DISPLAY_FIELDS deleted, field display is schema-driven.
  {
    id: "subset-renderers",
    type: "subset",
    description: "Renderers should only reference classified collections",
    subject: "renderers",
    reference: "classification-map",
  },
] as const;
